import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAdminMode } from '../context/AdminModeContext';
import { getAlerts, getOrganization, getOrganizations } from '../lib/api';
import { Button } from './ui/button';
import { SuperAdminBar } from './SuperAdminBar';
import { ExplainerVideo } from './ExplainerVideo';
import { 
  LayoutDashboard, 
  Shield, 
  AlertTriangle, 
  Calendar, 
  FileText, 
  Users, 
  Bell,
  Building2,
  ClipboardCheck,
  BookOpen,
  GraduationCap,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Search,
  Menu,
  Activity,
  UserPlus,
  Briefcase,
  Handshake,
  BookMarked
} from 'lucide-react';
import { cn } from '../lib/utils';

// Navigation items for Super Admins (includes Dashboard for Portfolio view)
const superAdminNavItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/org-iq', icon: Search, label: 'Risk Overview' },
  { path: '/assessments', icon: ClipboardCheck, label: 'Safety Assessment' },
  { path: '/policies', icon: BookOpen, label: 'Safety Policies' },
  { path: '/incidents', icon: AlertTriangle, label: 'Incidents' },
  { path: '/loss-runs', icon: Activity, label: 'Loss Runs' },
];

// Navigation items for Org Admins (no Dashboard - Org IQ is primary)
const orgAdminNavItems = [
  { path: '/org-iq', icon: Search, label: 'Risk Overview' },
  { path: '/assessments', icon: ClipboardCheck, label: 'Safety Assessment' },
  { path: '/policies', icon: BookOpen, label: 'Safety Policies' },
  { path: '/incidents', icon: AlertTriangle, label: 'Incidents' },
  { path: '/loss-runs', icon: Activity, label: 'Loss Runs' },
];

const adminNavItems = [
  { path: '/organizations', icon: Building2, label: 'Organizations' },
  { path: '/users', icon: Users, label: 'User Management' },
  { path: '/broker', icon: Briefcase, label: 'Broker Dashboard' },
];

// Navigation items for Brokers - same as super admin but scoped to their organizations
const brokerNavItems = [
  { path: '/broker', icon: Briefcase, label: 'Broker Dashboard' },
  { path: '/dashboard', icon: LayoutDashboard, label: 'Portfolio Overview' },
  { path: '/org-iq', icon: Search, label: 'Risk Overview' },
  { path: '/assessments', icon: ClipboardCheck, label: 'Safety Assessment' },
  { path: '/policies', icon: BookOpen, label: 'Safety Policies' },
  { path: '/incidents', icon: AlertTriangle, label: 'Incidents' },
  { path: '/loss-runs', icon: Activity, label: 'Loss Runs' },
  { path: '/organizations', icon: Building2, label: 'My Organizations' },
];

// Nav items for org admins with child organizations (national/regional level)
const orgAdminMemberItems = [  { path: '/network-intelligence', icon: Activity, label: 'Network Intelligence' },
  { path: '/organizations', icon: Building2, label: 'Member Organizations' },
];

// Resources section — visible to all roles (Partner Marketplace + Guidance Library)
const resourcesNavItems = [
  { path: '/partners', icon: Handshake, label: 'Partner Marketplace' },
  { path: '/guidance', icon: BookMarked, label: 'Guidance Library' },
];

// Maps a route to its explainer-video section key. Rendered once in Layout so the
// slot appears on every section in all view states (above the page content).
const SECTION_BY_PATH = {
  '/dashboard': 'dashboard',
  '/org-iq': 'risk-overview',
  '/monitoring': 'risk-overview',
  '/assessments': 'assessments',
  '/policies': 'policies',
  '/incidents': 'incidents',
  '/loss-runs': 'loss-runs',
  '/organizations': 'organizations',
  '/users': 'users',
  '/broker': 'broker',
  '/partners': 'partners',
  '/guidance': 'guidance',
  '/alerts': 'alerts',
  '/settings': 'settings',
  '/network-intelligence': 'network-intelligence',
  '/lms': 'training',
};

const resolveExplainerSection = (pathname) =>
  SECTION_BY_PATH[pathname] ||
  Object.entries(SECTION_BY_PATH).find(([p]) => pathname.startsWith(p + '/'))?.[1] ||
  null;

// NavLink component moved outside Layout to avoid re-creating on every render
const NavLink = ({ item, isActive, collapsed, onMobileClose }) => {
  return (
    <Link
      to={item.path}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors",
        isActive 
          ? "bg-primary/10 text-primary border-l-2 border-primary" 
          : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
      )}
      onClick={onMobileClose}
    >
      <item.icon className="w-5 h-5 flex-shrink-0" />
      {!collapsed && <span className="text-sm font-medium">{item.label}</span>}
    </Link>
  );
};

export const Layout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [orgLogo, setOrgLogo] = useState(null);
  const [hasChildOrgs, setHasChildOrgs] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isSuperAdmin, isOrgAdmin, isBroker } = useAuth();
  const { selectedOrg, isProspectOrg, canAccessModule } = useAdminMode();

  const handleMobileClose = useCallback(() => setMobileOpen(false), []);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await getAlerts({ is_read: false });
        setUnreadAlerts(response.data.length);
      } catch (error) {
        console.error('Failed to fetch alerts', error);
      }
    };
    fetchAlerts();
  }, []);

  // Check if org admin has child organizations (for national/regional admins)
  useEffect(() => {
    const checkChildOrgs = async () => {
      if (!isSuperAdmin() && user?.organization_id) {
        try {
          const response = await getOrganizations();
          // If there are more than 1 org, this admin has child orgs
          setHasChildOrgs(response.data.length > 1);
        } catch (error) {
          console.error('Failed to check child orgs', error);
        }
      }
    };
    checkChildOrgs();
  }, [user?.organization_id, isSuperAdmin]);

  // Fetch organization logo - for org admins or when super admin has selected an org
  useEffect(() => {
    const fetchOrgLogo = async () => {
      // Get org ID from either: selected org (super admin in org mode) or user's org (org admin)
      const orgId = selectedOrg?.id || (!isSuperAdmin() ? user?.organization_id : null);
      
      if (orgId) {
        try {
          const response = await getOrganization(orgId);
          if (response.data?.logo_url) {
            setOrgLogo(response.data.logo_url);
          } else {
            setOrgLogo(null);
          }
        } catch (error) {
          console.error('Failed to fetch org logo', error);
          setOrgLogo(null);
        }
      } else {
        setOrgLogo(null);
      }
    };
    fetchOrgLogo();
  }, [user?.organization_id, isSuperAdmin, selectedOrg?.id]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed lg:static inset-y-0 left-0 z-50 flex flex-col sidebar transition-all duration-300",
        collapsed ? "w-16" : "w-64",
        mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        {/* Logo - Always shows Players Health platform logo in sidebar */}
        <div className="h-16 flex items-center gap-3 px-4 border-b border-border">
          <img 
            src="https://customer-assets.emergentagent.com/job_safetyguard-4/artifacts/97rz3phl_players-health-lockup-color.png"
            alt="Players Health" 
            className="h-8 w-auto"
            data-testid="platform-logo"
          />
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {/* Broker users only see their dashboard */}
          {isBroker() && (
            <>
              {brokerNavItems.map((item) => (
                <NavLink 
                  key={item.path} 
                  item={item}
                  isActive={location.pathname === item.path}
                  collapsed={collapsed}
                  onMobileClose={handleMobileClose}
                />
              ))}
            </>
          )}
          
          {/* Use different nav items based on role - Super Admin sees Dashboard, Org Admin does not */}
          {/* Filter nav items based on module access for prospect organizations */}
          {!isBroker() && (isSuperAdmin() ? superAdminNavItems : orgAdminNavItems)
            .filter(item => canAccessModule(item.path))
            .map((item) => (
            <NavLink 
              key={item.path} 
              item={item} 
              isActive={location.pathname === item.path || 
                (item.path === '/org-iq' && location.pathname === '/monitoring')}
              collapsed={collapsed}
              onMobileClose={handleMobileClose}
            />
          ))}
          
          {/* Member Organizations section - for org admins with child organizations */}
          {!isSuperAdmin() && !isBroker() && hasChildOrgs && (
            <>
              <div className="my-4 border-t border-border" />
              <div className={cn("px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider", collapsed && "hidden")}>
                Network
              </div>
              {orgAdminMemberItems.map((item) => (
                <NavLink 
                  key={item.path} 
                  item={item}
                  isActive={location.pathname === item.path}
                  collapsed={collapsed}
                  onMobileClose={handleMobileClose}
                />
              ))}
            </>
          )}
          
          {/* Resources section - visible to all roles */}
          <div className="my-4 border-t border-border" />
          <div className={cn("px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider", collapsed && "hidden")}>
            Resources
          </div>
          {resourcesNavItems.map((item) => (
            <NavLink
              key={item.path}
              item={item}
              isActive={location.pathname === item.path}
              collapsed={collapsed}
              onMobileClose={handleMobileClose}
            />
          ))}

          {/* Admin section - only for super admins */}
          {isSuperAdmin() && (
            <>
              <div className="my-4 border-t border-border" />
              <div className={cn("px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider", collapsed && "hidden")}>
                Admin
              </div>
              {adminNavItems.map((item) => (
                <NavLink 
                  key={item.path} 
                  item={item}
                  isActive={location.pathname === item.path}
                  collapsed={collapsed}
                  onMobileClose={handleMobileClose}
                />
              ))}
            </>
          )}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-border">
          {!collapsed && (
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                <span className="text-sm font-medium">{user?.name?.[0]?.toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.role}</p>
              </div>
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-button"
            className={cn("w-full justify-start text-muted-foreground hover:text-foreground", collapsed && "justify-center")}
          >
            <LogOut className="w-4 h-4" />
            {!collapsed && <span className="ml-2">Logout</span>}
          </Button>
        </div>

        {/* Collapse button */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-secondary border border-border rounded-full items-center justify-center hover:bg-muted transition-colors"
        >
          {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronLeft className="w-3 h-3" />}
        </button>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Super Admin Bar - Global mode toggle */}
        <SuperAdminBar />
        
        {/* Header */}
        <header className="h-16 header-glass sticky top-0 z-30 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 text-muted-foreground hover:text-foreground"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-semibold font-['Barlow_Condensed'] uppercase tracking-tight">
              {(isSuperAdmin() ? superAdminNavItems : orgAdminNavItems).find(item => item.path === location.pathname)?.label || 
               adminNavItems.find(item => item.path === location.pathname)?.label ||
               resourcesNavItems.find(item => item.path === location.pathname)?.label ||
               'Dashboard'}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/alerts"
              data-testid="alerts-button"
              className="relative p-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Bell className="w-5 h-5" />
              {unreadAlerts > 0 && (
                <span className="absolute top-0 right-0 w-5 h-5 bg-destructive text-destructive-foreground text-xs rounded-full flex items-center justify-center">
                  {unreadAlerts > 9 ? '9+' : unreadAlerts}
                </span>
              )}
            </Link>
            <Link
              to="/settings"
              data-testid="settings-button"
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Settings className="w-5 h-5" />
            </Link>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {resolveExplainerSection(location.pathname) && (
            <ExplainerVideo section={resolveExplainerSection(location.pathname)} />
          )}
          {children}
        </main>
      </div>
    </div>
  );
};
